/**
 * Phase 3F: Governance Audit Logger
 * Comprehensive logging for all Phase 3 events
 */

import { supabase } from '@/integrations/supabase/client';
import type { Phase3AuditEventType } from './types';

interface AuditLogParams {
  eventType: Phase3AuditEventType;
  actorId: string;
  actorRole?: string;
  clientId?: string;
  surgeonId?: string;
  insightType?: string;
  insightId?: string;
  featureName?: string;
  previousValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

/**
 * Log a Phase 3 audit event
 */
export async function logPhase3Event(params: AuditLogParams): Promise<void> {
  try {
    const { error } = await supabase.from('phase3_audit_log').insert([{
      event_type: params.eventType,
      actor_id: params.actorId,
      actor_role: params.actorRole,
      client_id: params.clientId,
      surgeon_id: params.surgeonId,
      insight_type: params.insightType,
      insight_id: params.insightId,
      feature_name: params.featureName,
      previous_value: params.previousValue ? JSON.parse(JSON.stringify(params.previousValue)) : null,
      new_value: params.newValue ? JSON.parse(JSON.stringify(params.newValue)) : null,
      metadata: params.metadata ? JSON.parse(JSON.stringify(params.metadata)) : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    }]);

    if (error) {
      console.warn('Failed to log Phase 3 audit event:', error);
    }
  } catch (err) {
    console.warn('Error logging Phase 3 audit event:', err);
  }
}

/**
 * Log insight shown event
 */
export async function logInsightShown(
  actorId: string,
  actorRole: string,
  insightType: string,
  insightId: string,
  clientId?: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await logPhase3Event({
    eventType: 'insight_shown',
    actorId,
    actorRole,
    clientId,
    insightType,
    insightId,
    metadata,
  });
}

/**
 * Log insight dismissed event
 */
export async function logInsightDismissed(
  actorId: string,
  actorRole: string,
  insightType: string,
  insightId: string,
  clientId?: string,
  reason?: string
): Promise<void> {
  await logPhase3Event({
    eventType: 'insight_dismissed',
    actorId,
    actorRole,
    clientId,
    insightType,
    insightId,
    metadata: reason ? { dismissalReason: reason } : undefined,
  });
}

/**
 * Log insight override event
 */
export async function logInsightOverride(
  actorId: string,
  actorRole: string,
  insightType: string,
  insightId: string,
  previousValue: Record<string, unknown>,
  newValue: Record<string, unknown>,
  clientId?: string,
  reason?: string
): Promise<void> {
  await logPhase3Event({
    eventType: 'insight_override',
    actorId,
    actorRole,
    clientId,
    insightType,
    insightId,
    previousValue,
    newValue,
    metadata: reason ? { overrideReason: reason } : undefined,
  });
}

/**
 * Log feature flag change event
 */
export async function logFeatureFlagChanged(
  actorId: string,
  actorRole: string,
  featureName: string,
  previousValue: boolean,
  newValue: boolean
): Promise<void> {
  await logPhase3Event({
    eventType: 'feature_flag_changed',
    actorId,
    actorRole,
    featureName,
    previousValue: { enabled: previousValue },
    newValue: { enabled: newValue },
  });
}

/**
 * Log client opt-in/opt-out event
 */
export async function logClientOptInChange(
  actorId: string,
  actorRole: string,
  clientId: string,
  optedIn: boolean,
  acknowledgmentText?: string
): Promise<void> {
  await logPhase3Event({
    eventType: optedIn ? 'client_opted_in' : 'client_opted_out',
    actorId,
    actorRole,
    clientId,
    metadata: acknowledgmentText ? { acknowledgmentText } : undefined,
  });
}

/**
 * Log surgeon opt-in/opt-out event
 */
export async function logSurgeonOptInChange(
  actorId: string,
  surgeonId: string,
  optedIn: boolean,
  clientId?: string,
  acknowledgmentText?: string
): Promise<void> {
  await logPhase3Event({
    eventType: optedIn ? 'surgeon_opted_in' : 'surgeon_opted_out',
    actorId,
    actorRole: 'surgeon',
    clientId,
    surgeonId,
    metadata: acknowledgmentText ? { acknowledgmentText } : undefined,
  });
}

/**
 * Log disclaimer accepted event
 */
export async function logDisclaimerAccepted(
  surgeonId: string,
  clientId?: string
): Promise<void> {
  await logPhase3Event({
    eventType: 'disclaimer_accepted',
    actorId: surgeonId,
    actorRole: 'surgeon',
    clientId,
    surgeonId,
  });
}

/**
 * Log report generated event
 */
export async function logReportGenerated(
  actorId: string,
  actorRole: string,
  clientId: string,
  reportType: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await logPhase3Event({
    eventType: 'report_generated',
    actorId,
    actorRole,
    clientId,
    featureName: reportType,
    metadata,
  });
}

/**
 * Log report exported event
 */
export async function logReportExported(
  actorId: string,
  actorRole: string,
  clientId: string,
  reportType: string,
  exportFormat: 'pdf' | 'csv',
  metadata?: Record<string, unknown>
): Promise<void> {
  await logPhase3Event({
    eventType: 'report_exported',
    actorId,
    actorRole,
    clientId,
    featureName: reportType,
    metadata: { ...metadata, exportFormat },
  });
}
